import { GrDialog, GrDialogProps, GrIcon, GrTypography } from "@jnpmedi/grebob";
import { useQueryClient } from "@tanstack/react-query";
import { MvnMessage } from "@newLibs/utils";
import { UseBoolean } from "@newHooks/common/useBoolean";
import { notify } from "@newHooks/context/snackbar/notify";
import { useRemoveFolderFileMutation } from "@newHooks/mutation/folderFile/removeFolderFile.mutation";
import { FolderKey } from "@newHooks/query/folder/key";
import { useSharingRole } from "./hooks/useSharingRole";
import { SharingRoleGuardView } from "./SharingRoleGuardView";
import { DOCS_FOLDER_LANG } from "@maven-msg/lang/docs";
import { FolderType } from "@maven-rest/docs/Folder.dto";
import { FolderSharingRole } from "@maven-rest/docs/FolderSharing.dto";

interface RemoveFileDialogProps {
  open: UseBoolean;
  fileId: bigint;
  name: string;
  folderType?: FolderType;
  size?: GrDialogProps["size"];
}

export function RemoveFileDialog({ open, fileId, name, size = "xs" }: RemoveFileDialogProps) {
  const queryClient = useQueryClient();

  const { isAuthorized, myRole, error, isLoading } = useSharingRole({ fileId, role: FolderSharingRole.Manager });

  const { mutate: removeFileRequest, isLoading: isRemoveFileLoading } = useRemoveFolderFileMutation(
    { fileId },
    {
      onSuccess: () => {
        open.onFalse();
        queryClient.invalidateQueries({ queryKey: [FolderKey.BrowseFolders] });

        notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_ITEM_DELETE_SUCC), "SUCC");
      },
      onError: (error) => {
        notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_FILE_DELETE_FAIL, { message: error.responseMessage }), "ERROR");
      },
    }
  );

  const handleDialogClose = () => {
    open.onFalse();
  };

  const handleConfirmRemoveFile = () => {
    if (isRemoveFileLoading) return;

    removeFileRequest({ fileId });
  };

  return (
    <GrDialog
      title={MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_FILE_DELETE_HEADER)}
      titleIcon={<GrIcon type="alert-hexagon-filled" color="negative-primary" size={24} />}
      size={size}
      open={open.value}
      onClose={handleDialogClose}
      onConfirm={handleConfirmRemoveFile}
      confirmButton={{
        isLoading: isRemoveFileLoading,
        children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_DELETE_NOUN),
        disabled: isAuthorized !== true,
        color: "red",
      }}
      cancelButton={{ children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_CANCEL_NOUN) }}
    >
      <SharingRoleGuardView isAuthorized={isAuthorized} myRole={myRole} error={error} isLoading={isLoading}>
        {() => (
          <GrTypography type="Content.Body03" color="grey070">
            {MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_FILE_DELETE_DESCRIPTION, { name })}
          </GrTypography>
        )}
      </SharingRoleGuardView>
    </GrDialog>
  );
}
